// employee-leave-balance.component.ts
// REMOVED: Inline EmployeeLeaveBalance interface (→ models/leave.model.ts)
// REMOVED: Inline mock data (→ mocks/leave.mock.ts)
// REMOVED: Inline cell renderer (→ utils/grid-cell-renderers.ts)

import { Component, OnInit, input, inject, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ErsGridComponent } from '@rcs/grid';
import { MonthYearPickerComponent, MonthYearChange } from '../../month-year-picker/month-year-picker.component';
import { EmployeeCellRendererComponent } from '../../employee-cell-renderer/employee-cell-renderer.component';
import { LeaveBalanceDetailComponent } from '../../leave-balance-detail/leave-balance-detail.component';
import { CompOffRequest, EmployeeLeaveBalance } from '../../../models/leave.model';
import { alWithAlertRenderer } from '../../../utils/grid-cell-renderers';
import { formatDateMDY, formatDateLong as _formatDateLong, getDayName, parseLocalDate } from '../../../utils/date.utils';
import { ColDef, ColGroupDef, GridOptions, GridApi } from 'ag-grid-enterprise';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LeaveService } from '../../../services/leave.service';
import { LeaveRequest } from '../../../models/leave.model';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'ers-employee-leave-balance',
  standalone: true,
  imports: [
    TranslocoPipe,CommonModule, ErsGridComponent, MonthYearPickerComponent, MatButtonModule, MatTooltipModule],
  templateUrl: './employee-leave-balance.component.html',
  styleUrls: ['./employee-leave-balance.component.scss'],
})
export class EmployeeLeaveBalanceComponent implements OnInit {
  private leaveService = inject(LeaveService);

  isManagerView = input(false);
  private managerEmployeeId: string | null = null;

  columnDefs: (ColDef | ColGroupDef)[] = [];
  rowData: EmployeeLeaveBalance[] = [];
  customGridOptions: Partial<GridOptions> = {};
  private gridApi!: GridApi;

  currentMonth = new Date().getMonth();
  currentYear  = new Date().getFullYear();
  isLoading = false;
  totalEmployees = 0;
  totalPaidDays  = 0;

  private static readonly CENTERED_BOLD: any = { textAlign: 'center', fontWeight: '500' };

  constructor() {
    effect(() => {
      const _ = this.isManagerView();
      if (this.managerEmployeeId !== null) this.loadData();
    });
  }

  ngOnInit(): void {
    this.setupColumnDefs();
    this.setupGridOptions();
    this.leaveService.selectedUserTenant$.pipe(take(1)).subscribe(t => {
      this.managerEmployeeId = String(t?.['employeeId'] ?? '').trim() || null;
      this.loadData();
    });
  }

  onPickerChange(event: MonthYearChange): void {
    this.currentMonth = event.month;
    this.currentYear  = event.year;
    this.loadData();
  }

  private setupColumnDefs(): void {
    const centered = EmployeeLeaveBalanceComponent.CENTERED_BOLD;

    this.columnDefs = [
      { headerName: 'Employee',   field: 'employeeName', sortable: true, filter: 'agSetColumnFilter', width: 220, cellRenderer: EmployeeCellRendererComponent, cellRendererParams: { showExpander: true } },
      { headerName: 'Emp ID',     field: 'empId',        sortable: true, filter: 'agSetColumnFilter', width: 100 },
      { headerName: 'Department', field: 'department',   sortable: true, filter: 'agSetColumnFilter', width: 160 },
      {
        headerName: 'Leave Balance',
        children: [
          { headerName: 'AL',  field: 'al',  sortable: true, filter: false, width: 70,  cellStyle: centered, cellRenderer: alWithAlertRenderer },
          { headerName: 'SL',  field: 'sl',  sortable: true, filter: false, width: 70,  cellStyle: centered },
          { headerName: 'LWP', field: 'lwp', sortable: true, filter: false, width: 70,  cellStyle: centered },
          {
            headerName: 'Total Available',
            field: 'totalAvailableBalance',
            sortable: true,
            filter: false,
            width: 130,
            cellStyle: { ...centered, color: '#059669', fontWeight: '600' },
            valueGetter: (params: any) => {
              const balances: any[] = params.data?.leaveTypeBalances ?? [];
              if (balances.length === 0) return 0;
              const hasAvailable = balances.some((b: any) => b.availableBalance != null);
              return balances.reduce((sum: number, b: any) =>
                sum + (hasAvailable ? (b.availableBalance ?? 0) : (b.remainingBalance ?? 0)), 0
              );
            },
            valueFormatter: (params: any) =>
              params.value != null ? Number(params.value).toFixed(2) : '0.00',
          },
        ],
      },
      {
        headerName: 'Pay Period',
        children: [
          { headerName: 'Month Days', field: 'monthDays', sortable: true, filter: false, width: 110, cellStyle: centered },
          { headerName: 'Paid Days',  field: 'paidDays',  sortable: true, filter: false, width: 100, cellStyle: centered },
        ],
      },
      {
        headerName: 'Leave Type',
        children: [
          { headerName: 'Comp Off',           field: 'compOff',  sortable: true, filter: false, width: 100, cellStyle: centered },
          { headerName: 'Overtime (In days)', field: 'overtime', sortable: true, filter: false, width: 150, cellStyle: centered },
        ],
      },
    ];
  }

  private setupGridOptions(): void {
    this.customGridOptions = {
      suppressCellFocus: true,
      rowHeight: 40,
      suppressMovableColumns: true,
      suppressRowHoverHighlight: false,
      enableCellTextSelection: false,
      masterDetail: true,
      detailCellRenderer: LeaveBalanceDetailComponent,
      detailCellRendererParams: {
        refreshStrategy: 'everything',
        onSaveRemarks: (employeeId: string, remark: string) => this.saveRemarks(employeeId, remark),
      },
      detailRowHeight: 500,
      isRowMaster: (dataItem: any) => {
        // All rows with employee data should be expandable
        return !!(dataItem?.employeeId || dataItem?.empId);
      },
      animateRows: false,
      suppressRowTransform: true,
      pagination: false,
      onGridReady: (params: any) => {
        this.gridApi = params.api;
        // Column widths are all fixed (no flex), so they don't reach the
        // container's edge on wide screens; stretch them proportionally to
        // fill it instead of leaving a dead gap on the right.
        params.api.sizeColumnsToFit();
      },
      onGridSizeChanged: (params: any) => {
        params.api.sizeColumnsToFit();
      },
      onRowGroupOpened: (params: any) => {
        if (!params.expanded) return;
        params.api.forEachNode((node: any) => {
          if (node.expanded && node.id !== params.node.id) {
            node.setExpanded(false);
          }
        });
      },
    };
  }

  private loadData(): void {
    this.isLoading = true;
    // month is 1-based: getMonth() returns 0-based so add 1
    forkJoin({
      summaryRows: this.leaveService.getTenantLeaveBalanceSummary(
        this.currentMonth + 1,
        this.currentYear,
        this.isManagerView() ? this.managerEmployeeId ?? undefined : undefined,
      ),
      leaveRequests: this.leaveService.getAllLeaveRequestsForHR().pipe(
        catchError(() => of([] as LeaveRequest[])),
      ),
      compOffRequests: this.leaveService.getAllCompOffRequestsForHR().pipe(
        catchError(() => of([] as CompOffRequest[])),
      ),
      holidays: this.leaveService.getHolidays().pipe(
        catchError(() => of([] as any[])),
      ),
    }).subscribe(({ summaryRows, leaveRequests, compOffRequests, holidays }) => {
        this.isLoading = false;
        const plannedByEmployee = this.buildPlannedLeavesMap(leaveRequests ?? []);
        const optionalItems = this.buildOptionalHolidayPreviewItems(holidays ?? []);
        const compOffByEmployee = this.buildCompOffDetailsMap(compOffRequests ?? []);

        this.rowData = (summaryRows ?? []).map((r: any): EmployeeLeaveBalance => {
          const employeeId =
            r.employeeId ??
            r.EMPLOYEE_ID ??
            r.leaveTypeBalances?.[0]?.employeeId ??
            r.leaveTypeBalances?.[0]?.EMPLOYEE_ID ??
            '';

          const compOffItems = compOffByEmployee.get(employeeId) ?? [];

          const compOffLeaveBalance = Number(
            (r.leaveTypeBalances ?? [])
              .find((b: any) => String(b.leaveTypeName ?? '').toLowerCase().includes('comp'))
              ?.availableBalance ??
            0,
          );
          const compOffTotal =
            compOffLeaveBalance > 0
              ? compOffLeaveBalance
              : (Number(r.compOff ?? 0) || compOffItems.filter(i => i.status === 'Approved').length);

          const resolvedRemark = r.notes ? String(r.notes) : (r.remarks ? String(r.remarks) : '');
          console.log('[LeaveBalance] employee:', r.employeeName, 'remarks from API:', resolvedRemark);

          return {
          id:           0,                            // no numeric id from SP
          employeeId,
          employeeName: r.employeeName  ?? '',
          employeeRole: '',                           // not in SP
          employeeAvatar: '',                         // not in SP
          empId:        r.employeeNumber ?? r.employeeId ?? '',
          department:   r.department    ?? '',
          al:           r.al            ?? 0,
          sl:           r.sl            ?? 0,
          lwp:          r.lwp           ?? 0,
          monthDays:    r.monthDays     ?? 0,
          paidDays:     r.paidDays      ?? 0,
          compOff:      compOffTotal,
          overtime:     r.overtime      ?? 0,
          // detail panel data
          leaveTypeBalances: r.leaveTypeBalances ?? [],
          plannedLeaves: plannedByEmployee.get(employeeId) ?? [],
          optionalHolidays: optionalItems,
          optionalHolidayTotal: optionalItems.length,
          optionalHolidayNote: '',
          compOffDays: compOffItems,
          compOffTotal,
          remarks: resolvedRemark
            ? { text: resolvedRemark, hasInfo: true }
            : { text: '', hasInfo: false },
        };
      });

        this.totalEmployees = this.rowData.length;
        this.totalPaidDays  = this.rowData.reduce((sum, row) => sum + row.paidDays, 0);
      });
  }

  private async saveRemarks(employeeId: string, remark: string): Promise<void> {
    const id = String(employeeId ?? '').trim();
    if (!id) return;

    const value = String(remark ?? '').trim();
    await firstValueFrom(this.leaveService.saveEmployeeLeaveBalanceRemark({
      employeeId: id,
      month: this.currentMonth + 1,
      year: this.currentYear,
      notes: value,
    }));

    // Reload data from server to get all remarks including the newly saved one
    this.loadData();
  }

  private buildOptionalHolidayPreviewItems(
    holidays: any[],
  ): Array<{ name: string; date: string; day: string; type: string }> {
    return (holidays ?? [])
      .filter((holiday: any) => holiday?.isOptional === true)
      .map((holiday: any) => {
        const d = this.toDate(holiday.holidayDate);
        return {
          name: String(holiday.holidayName ?? ''),
          date: d ? this.formatDateLong(d) : '-',
          day: d ? getDayName(d) : '-',
          type: 'Optional',
        };
      })
      .sort((a, b) => {
        const da = this.toDate(a.date);
        const db = this.toDate(b.date);
        return (da?.getTime() ?? 0) - (db?.getTime() ?? 0);
      });
  }

  private buildCompOffDetailsMap(
    requests: CompOffRequest[],
  ): Map<string, Array<{ workDate: string; reqDate: string; duration: string; status: string }>> {
    const map = new Map<string, Array<{ workDate: string; reqDate: string; duration: string; status: string }>>();

    for (const req of requests) {
      const empId = String((req as any).employeeId ?? req.userId ?? '').trim();
      if (!empId) continue;

      const list = map.get(empId) ?? [];
      list.push({
        workDate: req.dateWorked ? this.formatDateRange(req.dateWorked) : '-',
        reqDate: req.compOffDate ? this.formatDateRange(req.compOffDate) : '-',
        duration: '-',
        status: String(req.status ?? 'Pending'),
      });
      map.set(empId, list);
    }

    return map;
  }

  private buildPlannedLeavesMap(
    requests: LeaveRequest[],
  ): Map<string, Array<{ date: string; status?: string }>> {
    const map = new Map<string, Array<{ date: string; status?: string }>>();

    for (const req of requests) {
      if (req.isHourlyLeaveRequest) continue;

      const empId = String(req.userId ?? '').trim();
      if (!empId) continue;

      const status = String(req.status ?? '').toLowerCase();
      if (status === 'rejected' || status === 'cancelled') continue;

      const start = this.toDate(req.startDate);
      const end = this.toDate(req.endDate);
      if (!start || !end) continue;

      const effectiveStart = start <= end ? start : end;
      const effectiveEnd = start <= end ? end : start;
      const days = this.expandDateRange(effectiveStart, effectiveEnd, 31);
      if (days.length === 0) continue;

      const list = map.get(empId) ?? [];
      const isOptionalType = String(req.leaveTypeName ?? '').toLowerCase().includes('optional');

      for (const day of days) {
        list.push({
          date: this.formatDateDMY(day),
          status: isOptionalType ? 'Optional' : undefined,
        });
      }

      map.set(empId, list);
    }

    for (const [empId, list] of map) {
      const uniqueByDate = new Map<string, { date: string; status?: string }>();
      for (const item of list) {
        if (!uniqueByDate.has(item.date)) {
          uniqueByDate.set(item.date, item);
        }
      }

      const sorted = Array.from(uniqueByDate.values()).sort((a, b) => {
        const da = this.toDate(a.date);
        const db = this.toDate(b.date);
        return (db?.getTime() ?? 0) - (da?.getTime() ?? 0);
      });

      map.set(empId, sorted.slice(0, 8));
    }

    return map;
  }

  private expandDateRange(start: Date, end: Date, maxDays: number): Date[] {
    const result: Date[] = [];
    const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());

    while (cursor.getTime() <= last.getTime() && result.length < maxDays) {
      result.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }

    return result;
  }

  private toDate(value: string | Date | null | undefined): Date | null {
    if (!value) return null;
    const d = parseLocalDate(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private formatDateDMY(date: Date): string {
    return formatDateMDY(date);
  }

  private formatDateLong(date: Date): string {
    return _formatDateLong(date);
  }

  private formatDateRange(value: string | Date): string {
    const parsed = this.toDate(value);
    return parsed ? this.formatDateDMY(parsed) : String(value ?? '-');
  }
}